import React from "react";
import { Avatar, Card, CardContent, Tooltip, Typography } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(2),
    height: "100%",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25),
      gap: theme.spacing(1),
      alignItems: "flex-start",
    },
  },
  avatar: (props) => ({
    fontSize: "28px",
    color: props.color || theme.palette.primary.main,
    // Fundo com 12% de opacidade da cor — funciona em dark e light mode
    backgroundColor: props.color
      ? `${props.color}1f`
      : theme.palette.type === "dark"
      ? "#2f3646"
      : "#f0f0f0",
    width: 52,
    height: 52,
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: 34,
      height: 34,
      fontSize: "18px",
    },
  }),
  content: {
    padding: 0,
    "&:last-child": { paddingBottom: 0 },
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
    overflowWrap: "anywhere",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.68rem",
      letterSpacing: "0.02em",
    },
  },
  value: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.15rem",
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: 2,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
  },
  delta: {
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 700,
    marginLeft: -4,
  },
  good: { color: theme.palette.success ? theme.palette.success.main : "#2e7d32" },
  bad: { color: theme.palette.error.main },
  neutral: { color: theme.palette.text.secondary },
}));

/**
 * Card de indicador.
 * `delta` (opcional): { value: número em %, good: true | false | null }.
 * `hint` (opcional): texto curto abaixo do valor.
 * `tooltip` (opcional): explicação da métrica ao passar o mouse.
 */
export default function CardCounter({ icon, title, value, loading, color, delta, hint, tooltip }) {
  const classes = useStyles({ color });

  if (loading) return <Skeleton variant="rect" height={80} style={{ borderRadius: 8 }} />;

  const deltaClass =
    delta && delta.good === true ? classes.good : delta && delta.good === false ? classes.bad : classes.neutral;

  const card = (
    <Card style={{ height: "100%" }}>
      <div className={classes.card}>
        <Avatar className={classes.avatar}>{icon}</Avatar>
        <CardContent className={classes.content}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.value}>
            {value ?? "—"}
          </Typography>
          {(delta || hint) && (
            <div className={classes.footer}>
              {delta && (
                <span className={`${classes.delta} ${deltaClass}`}>
                  {delta.value > 0 ? (
                    <ArrowDropUp fontSize="small" />
                  ) : delta.value < 0 ? (
                    <ArrowDropDown fontSize="small" />
                  ) : null}
                  {delta.value > 0 ? "+" : ""}
                  {delta.value}%
                </span>
              )}
              {hint && <span>{hint}</span>}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <div style={{ height: "100%" }}>{card}</div>
    </Tooltip>
  ) : (
    card
  );
}
